import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";

/** Menu contract from ApiV2 App/Menu(V4) — same shape legacy includes/menu.php consumes from API_MENU. */
export interface MenuSub { ID?: number; Name: string; PathUrl: string; Location?: string }
export interface MenuItem { MenuName: string; MenuIcon: string; SubMenu: MenuSub[]; PageTitles?: { Name: string; PathUrl: string; Title: string; Location: string }[] }

export function useMenu() {
  return useQuery({ queryKey: ["menu"], queryFn: async () => (await api.get<{ Error: string | null; Data: MenuItem[] }>("/app/menu")).data, staleTime: 5 * 60_000 });
}

/** Legacy PathUrl "Purchase/create-booking" -> React route "/v3/Purchase/create-booking" */
export const legacyPath = (p: string) => `/v3/${p.replace(/^\/+/, "").replace(/\.php$/, "")}`;

/** Fallback menu — used ONLY while the real role/menu (admin) DB is unreachable. Lists exactly the screens that
 * are actually migrated & routed today; nothing invented. Same MenuItem/SubMenu shape the real API_MENU
 * response would return, rendered through the identical markup below. */
const FALLBACK_MENU: MenuItem[] = [
  { MenuName: "Sales", MenuIcon: "ri-shopping-cart-2-line", SubMenu: [
    { Name: "Sales Orders (PI List)", PathUrl: "Sales/pi-list" },
    { Name: "Buyers", PathUrl: "Sales/buyers" },
    { Name: "Consignees", PathUrl: "Sales/consignees" },
    { Name: "Banks", PathUrl: "Sales/banks" },
    { Name: "Clearing Agent Status", PathUrl: "Sales/clearing-agent-status" },
  ] },
  { MenuName: "Purchase", MenuIcon: "ri-shopping-basket-2-line", SubMenu: [
    { Name: "New Booking", PathUrl: "Purchase/create-booking" },
    { Name: "Booking List", PathUrl: "Purchase/rm-booking-list" },
    { Name: "New Arrival", PathUrl: "Purchase/create-arrival" },
    { Name: "Arrival List", PathUrl: "Purchase/rm-arrival-list" },
    { Name: "Lots List", PathUrl: "Purchase/LotsList" },
    { Name: "Lots Track", PathUrl: "Purchase/rm-lots-track" },
    { Name: "Bills List", PathUrl: "Purchase/rm-bills-list" },
    { Name: "Suppliers List", PathUrl: "Purchase/supplier-list" },
    { Name: "Payments", PathUrl: "Purchase/rm-payments" },
    { Name: "Payment Summary", PathUrl: "Purchase/rm-payment-summary" },
    { Name: "Agents", PathUrl: "Purchase/rm-agent-list" },
    { Name: "Financiers", PathUrl: "Purchase/rm-financier-list" },
    { Name: "Vehicles", PathUrl: "Purchase/VehicleList" },
    { Name: "Purchase Rates", PathUrl: "Purchase/AddPurchaseRates" },
  ] },
  { MenuName: "Quality Control", MenuIcon: "ri-shield-check-line", SubMenu: [
    { Name: "RM Analysis", PathUrl: "QualityControl/RmAnalysis" },
    { Name: "Antibiotic Reports", PathUrl: "QualityControl/DailyLabReports" },
    { Name: "Traceability", PathUrl: "QualityControl/TraceabilityList" },
    { Name: "Lab Names", PathUrl: "QualityControl/lab-names-list" },
    { Name: "Analysis Required", PathUrl: "QualityControl/analysis-required-list" },
  ] },
  { MenuName: "Preprocessing", MenuIcon: "ri-scissors-cut-line", SubMenu: [
    { Name: "Tracks", PathUrl: "PreProcessing/lot-tracking" },
    { Name: "Receival", PathUrl: "PreProcessing/receival-page-trackid" },
    { Name: "Deheading", PathUrl: "PreProcessing/de-heading-trackid" },
    { Name: "Grading", PathUrl: "PreProcessing/grading-trackid" },
    { Name: "Value Addition", PathUrl: "PreProcessing/value-addition-trackid" },
    { Name: "Plant/Center Stations", PathUrl: "PreProcessing/ppc-stations-list" },
    { Name: "HL/VA Variety", PathUrl: "PreProcessing/hlva-variety-list" },
  ] },
  { MenuName: "Production", MenuIcon: "ri-snowy-line", SubMenu: [
    { Name: "Soaking", PathUrl: "Production/soaking-trackid" },
    { Name: "Freezing", PathUrl: "Production/freezing-trackid" },
  ] },
  { MenuName: "ColdStore", MenuIcon: "ri-temp-cold-line", SubMenu: [
    { Name: "Daily Production Data", PathUrl: "ColdStore/daily-production-data" },
    { Name: "Daily Inward", PathUrl: "ColdStore/daily-inward" },
    { Name: "Inward Approval", PathUrl: "ColdStore/admin-inward-approval" },
    { Name: "Stock Search", PathUrl: "ColdStore/store-stock-search" },
    { Name: "Stock Out", PathUrl: "ColdStore/stock-out" },
    { Name: "IBT Transfer", PathUrl: "ColdStore/ibt-transfer" },
    { Name: "Coldstores", PathUrl: "ColdStore/coldstores-list" },
    { Name: "Tenants", PathUrl: "ColdStore/tenants-list" },
    { Name: "Blocks", PathUrl: "ColdStore/blocks-list" },
    { Name: "Floors", PathUrl: "ColdStore/floors-list" },
  ] },
  { MenuName: "Admin", MenuIcon: "ri-shield-user-line", SubMenu: [
    { Name: "Users", PathUrl: "Admin/users-list" },
    { Name: "Access Roles", PathUrl: "Admin/edit-roles" },
  ] },
];

/** Primary-nav module catalog — matches the current production Camaroo UAT shell (screenshots: white header,
 * rounded module pills, per-module accent color when active). Chrome only (name/icon/accent) — real installs
 * filter this list down to whatever `/api/app/menu` (role/menu DB) permits; while that DB is unavailable here
 * we show the full catalog so the shell renders correctly, but only migrated modules are clickable.
 * `path` is the exact-cased URL segment used by the real React routes (may differ from `key`'s display casing
 * used for menu-name matching, e.g. "Quality Control" vs "QualityControl") — fixes module-root links 404ing
 * into the generic NotMigrated catch-all. */
const MODULE_CATALOG: { key: string; label: string; path: string; icon: string; accent: string }[] = [
  { key: "Sales", label: "Sales", path: "Sales", icon: "ri-shopping-cart-2-line", accent: "#7c4dff" },
  { key: "Purchase", label: "Purchase", path: "Purchase", icon: "ri-shopping-basket-2-line", accent: "#0d9488" },
  { key: "Preprocessing", label: "Preprocessing", path: "PreProcessing", icon: "ri-scissors-cut-line", accent: "#ec4899" },
  { key: "Quality Control", label: "Quality Control", path: "QualityControl", icon: "ri-shield-check-line", accent: "#f59e0b" },
  { key: "Production", label: "Production", path: "Production", icon: "ri-settings-4-line", accent: "#1f5fd6" },
  { key: "Coldstore", label: "Coldstore", path: "ColdStore", icon: "ri-snowy-line", accent: "#0ea5e9" },
  { key: "Inventory", label: "Inventory", path: "Inventory", icon: "ri-archive-line", accent: "#22c55e" },
  { key: "Admin", label: "Admin", path: "Admin", icon: "ri-shield-user-line", accent: "#64748b" },
];
const ENABLED_MODULES = new Set(["Sales", "Purchase", "Quality Control", "Preprocessing", "Production", "Coldstore", "Admin"]);
const norm = (s?: string | null) => (s || "").toLowerCase().replace(/\s+/g, "");
const ENABLED_MODULES_NORM = new Set(Array.from(ENABLED_MODULES, norm));

const initials = (name?: string | null) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Dropdown: React.FC<{ id: string; label: React.ReactNode; children: React.ReactNode; className?: string; testId?: string }> = ({ id, label, children, className, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", esc); };
  }, []);
  return (
    <div className={`cam-dd ${open ? "cam-dd-open" : ""} ${className || ""}`} ref={ref}>
      <button type="button" className="cam-dd-toggle" id={`topnav-${id}`} onClick={() => setOpen((o) => !o)} data-testid={testId || `menu-${id}`}>{label}</button>
      {open && <div className="cam-dd-menu" onClick={() => setOpen(false)}>{children}</div>}
    </div>
  );
};

/** TOP HEADER — dynamic tenant/client identity (never hardcoded), Settings, notifications, user + role, avatar.
 * Matches the current production UAT shell (light header, circular logo, "Powered by Camaroo" wordmark). */
const TopHeader: React.FC = () => {
  const { session, logout } = useAuth();
  return (
    <header className="cam-header" data-testid="cam-top-header">
      <Link to="/" className="cam-header-brand" data-testid="brand-home">
        <span className="cam-company-avatar" data-testid="company-avatar">{initials(session?.ClientName)}</span>
        <span className="cam-company-text">
          <span className="cam-company-name" data-testid="company-name">{session?.ClientName || "Camaroo ERP"}</span>
          <span className="cam-powered-by">Powered by <span className="cam-wordmark" style={{ fontSize: 12 }}>Camaroo</span></span>
        </span>
      </Link>
      <div className="cam-header-right">
        <button type="button" className="cam-header-btn" data-testid="settings-btn"><i className="ri-settings-3-line" /><span>Settings</span></button>
        <button type="button" className="cam-header-icon-btn" data-testid="notifications-btn"><i className="ri-notification-3-line" /><span className="cam-notif-dot" /></button>
        <Dropdown id="Account" testId="account-menu" className="cam-account-dd" label={
          <span className="cam-user-block">
            <span className="cam-user-text">
              <span className="cam-user-name" data-testid="header-user-name">{session?.UserName || "User"}</span>
              <span className="cam-user-role" data-testid="header-user-role">{String(session?.UserRoleID) === "0" ? "Super Admin" : "User"}</span>
            </span>
            <span className="cam-avatar" data-testid="user-avatar">{initials(session?.UserName)}</span>
          </span>}>
          <Link to="/ChangePassword" className="cam-dd-item" data-testid="menu-change-password">Change Password</Link>
          <a href="/auth-login" className="cam-dd-item" onClick={(e) => { e.preventDefault(); logout(); window.location.assign("/auth-login"); }} data-testid="menu-logout">Logout</a>
        </Dropdown>
      </div>
    </header>
  );
};

/** PRIMARY MODULE NAVIGATION — rounded pill row; active = solid per-module accent color. Filtered by role/menu
 * data when available; unmigrated modules stay visible (matches screenshots) but disabled. */
const PrimaryNav: React.FC<{ activeModule: string | null }> = ({ activeModule }) => {
  const menu = useMenu();
  const modules = menu.isError || !menu.data?.Data?.length
    ? MODULE_CATALOG
    : MODULE_CATALOG.filter((m) => menu.data!.Data.some((d) => norm(d.MenuName) === norm(m.key)));
  return (
    <nav className="cam-primary-nav" data-testid="primary-nav">
      {modules.map((m) => {
        const enabled = ENABLED_MODULES.has(m.key);
        const active = norm(activeModule) === norm(m.key);
        return (
          <Link key={m.key} to={enabled ? `/v3/${m.path}` : "#"} className={`cam-primary-pill ${active ? "cam-pill-active" : ""} ${!enabled ? "cam-pill-disabled" : ""}`}
            style={active ? { background: m.accent, borderColor: m.accent, boxShadow: `0 4px 10px ${m.accent}44` } : undefined}
            onClick={(e) => { if (!enabled) e.preventDefault(); }} data-testid={`primary-nav-${m.key.replace(/\s+/g, "-").toLowerCase()}`}>
            <i className={m.icon} />{m.label}
          </Link>
        );
      })}
    </nav>
  );
};

/** SECONDARY NAVIGATION — real submenu screens for the active module, rendered as light rounded pill tabs. */
const SecondaryNav: React.FC<{ activeModule: string | null }> = ({ activeModule }) => {
  const menu = useMenu();
  const loc = useLocation();
  if (!activeModule || !ENABLED_MODULES_NORM.has(norm(activeModule))) return null;
  const realMenu = (menu.data?.Data || []).find((d) => norm(d.MenuName) === norm(activeModule));
  const fallback = FALLBACK_MENU.find((d) => norm(d.MenuName) === norm(activeModule));
  const subScreens = (realMenu || fallback)?.SubMenu.map((s) => ({ name: s.Name, path: legacyPath(s.PathUrl) })) || [];
  if (!subScreens.length) return null;
  return (
    <nav className="cam-secondary-nav" data-testid="secondary-nav">
      {subScreens.map((s) => (
        <Link key={s.path} to={s.path} className={`cam-secondary-pill ${loc.pathname === s.path ? "cam-pill-active-light" : ""}`}
          data-testid={`secondary-nav-${s.path.split("/").pop()}`}>{s.name}</Link>
      ))}
    </nav>
  );
};

/** PAGE HEADER — Location > Title breadcrumb (matches legacy includes/page-header.php pattern). */
export const PageHeader: React.FC<{ title: string; location?: string }> = ({ title, location = "Utility" }) => (
  <div className="row">
    <div className="col-12">
      <div className="page-title-box d-sm-flex align-items-center justify-content-between">
        <h4 className="mb-sm-0" id="page-header" data-testid="page-header">{title}</h4>
        <div className="page-title-right">
          <ol className="breadcrumb m-0">
            <li className="breadcrumb-item" data-testid="breadcum-root"><a href="#!">{location}</a></li>
            <li className="breadcrumb-item active" data-testid="breadcum-next">{title}</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
);

export default function AppLayout() {
  const loc = useLocation();
  const activeModule = loc.pathname.match(/^\/v3\/([^/]+)/)?.[1] || null;
  return (
    <div id="layout-wrapper" className="cam-shell">
      <TopHeader />
      <PrimaryNav activeModule={activeModule} />
      <SecondaryNav activeModule={activeModule} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid"><Outlet /></div>
        </div>
        <footer className="footer"><div className="container-fluid"><div className="row"><div className="col-sm-6">{new Date().getFullYear()} © Camaroo. -- Version-3.0.0</div></div></div></footer>
      </div>
    </div>
  );
}


